import { theme, Tooltip } from 'antd';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ChevronUpIcon } from '@/components/Icons';

import type { SidebarMenuItemModel } from '../../types';
import * as SidebarStyle from '../SidebarMenu/styles';
import { TooltipMenu } from '../TooltipMenu';

import * as Styled from './styles';

interface Props {
  item: SidebarMenuItemModel;
  active: string | null;
  setActive: (key: string) => void;
  activeChild: string | null;
  setActiveChild: (key: string | null) => void;
  isMenuCollapsed?: boolean;
  setIsMenuCollapsed: Dispatch<SetStateAction<boolean>>;
}

export const MenuItem = ({
  item,
  setActive,
  active,
  setActiveChild,
  activeChild,
  isMenuCollapsed,
  setIsMenuCollapsed
}: Props) => {
  const { token } = theme.useToken();
  const [isMenuItemCollapsed, setIsMenuItemCollapsed] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const handleClickSubMenuItem = useCallback(
    (key: string, callback?: () => void) => () => {
      setActive(item.key);
      setActiveChild(key);
      setIsMenuCollapsed(true);
      setIsMenuItemCollapsed(true);
      setIsTooltipOpen(false);
      if (callback) callback();
    },
    [item, setActive, setActiveChild, setIsMenuCollapsed, setIsTooltipOpen]
  );

  const hanldeClickMenuItem = useCallback(() => {
    if (!item.children) {
      setActive(item.key);
      item.onClick?.();
      setActiveChild(null);
      setIsMenuCollapsed(true);
      setIsTooltipOpen(false);
    }

    if (item.children) {
      setIsMenuCollapsed(false);
      setIsMenuItemCollapsed(collapsed => !collapsed);
    }
  }, [item, setActive, setActiveChild, setIsMenuCollapsed]);

  const isActive = useMemo(() => {
    if (item.children) {
      const filteredWithChildren = item.children.map(item => item.key);
      return filteredWithChildren.includes(activeChild || '');
    }
    return active === item.key;
  }, [active, activeChild, item.children, item.key]);

  useEffect(() => {
    if (isMenuCollapsed && isMenuItemCollapsed) {
      setIsMenuItemCollapsed(false);
    }
  }, [isMenuCollapsed, isMenuItemCollapsed]);

  return (
    <>
      <Tooltip
        title={isMenuCollapsed ? <TooltipMenu label={item.label} /> : ''}
        placement='right'
        open={isTooltipOpen && isMenuCollapsed}
        onOpenChange={setIsTooltipOpen}>
        <Styled.SidebarMenuItem
          key={item.key}
          onClick={hanldeClickMenuItem}
          $isActive={isActive}
          $isMenuCollapsed={isMenuCollapsed}
          $color={token.Menu?.itemColor}
          $activeColor={token.Menu?.itemSelectedColor}
          $activeBackgroundColor={token.Menu?.itemSelectedBg}>
          <Styled.SidebarMenuItemActiveMark
            $backgroundColor={token.Menu?.itemSelectedColor}
            $isActive={isActive}
          />
          {item.icon}
          <span>{item.label}</span>
          {item.children && !isMenuCollapsed && (
            <Styled.CollapseButton
              $isOpen={isMenuItemCollapsed && !isMenuCollapsed}
              data-testid='collapse-button'>
              <ChevronUpIcon />
            </Styled.CollapseButton>
          )}
        </Styled.SidebarMenuItem>
      </Tooltip>

      {item.children && (
        <Styled.CollapseContent
          $isOpen={isMenuItemCollapsed && !isMenuCollapsed}>
          <SidebarStyle.SubMenuList>
            {item.children.map(child => (
              <Styled.SidebarSubMenuItem
                key={`child-${child.key}`}
                onClick={handleClickSubMenuItem(child.key, child.onClick)}
                $isActive={activeChild === child.key}
                $color={token.Menu?.itemColor}
                $activeColor={token.Menu?.itemSelectedColor}
                $activeBackgroundColor={token.Menu?.itemSelectedBg}>
                <span>{child.label}</span>
              </Styled.SidebarSubMenuItem>
            ))}
          </SidebarStyle.SubMenuList>
        </Styled.CollapseContent>
      )}
    </>
  );
};
